import { useUpdateStreamConsumer } from "@sinch/mcp-update-stream";
import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import { useUser as useFetchUser } from "../api/hooks/useUser";
import type { ShellUser } from "../api/types";
import { zodSafeParseMap } from "../helpers/zodSafeParseMap";
import {
  UpdateStreamUserUpdateZod,
  isUpdateStreamUserUpdateMessage,
  isUserUpdateRelatedToUser,
} from "./update-stream-user-update-message";

const UserContext = createContext<ShellUser | undefined>(undefined);
UserContext.displayName = "UserContext";

export function UserProvider({
  children,
  initialUser,
  Fallback,
}: {
  children: ReactNode;
  initialUser?: ShellUser;
  Fallback?: () => JSX.Element;
}) {
  const [user, fetchUser] = useFetchUser(initialUser);

  useUpdateStreamConsumer((message) => {
    if (isUpdateStreamUserUpdateMessage(message)) {
      zodSafeParseMap(UpdateStreamUserUpdateZod, {
        mapError: (err) => {
          console.error("UpdateStreamUserUpdateMessage parsing error:", err);
        },
        mapValid: (userUpdatedMessage) => {
          if (!isUserUpdateRelatedToUser(userUpdatedMessage, user)) {
            return;
          }
          // The data in UserMe and User are not the same.
          // In the UpdateStream we get User, but we want to update UserMe.
          fetchUser({
            // agent/me is cached in the browser, so we need to cancel it.
            cache: "reload",
          });
        },
      })(message);
    }
  });

  if (!user) {
    return Fallback ? <Fallback /> : null;
  }

  return <UserContext.Provider value={user}> {children} </UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
